/**
 * Pipeline & Facility AI drafts (batch 6, fieldiq-api#1236): names, skins,
 * who may press what, and how a refusal reads.
 *
 * NOTHING HERE COMPUTES A FIGURE. Hours, counts, flags and states are the
 * API's deterministic layer; this file words them. The AI's part is the draft
 * text only, shown as a draft until a person approves it.
 */
import { pfApiError, pfRefusal, type PfSkin, PF_UNKNOWN_SKIN } from './pipeline-facility';
import type { PfAiAgent } from '../types/pf-ai';

/** Mirrors fieldiq-api lib/pf-roles.ts PF_AI_ROLES (the agent-review roles). For hiding, not enforcing. */
export const PF_AI_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const canUsePfAi = (role: string | null | undefined): boolean => !!role && (PF_AI_ROLES as readonly string[]).includes(role);

export const PF_AI_AGENT_LABEL: Record<PfAiAgent, string> = {
  pf_daily_summary: 'Daily summary',
  pf_ticket_preflight: 'Preflight',
  pf_compliance_digest: 'Compliance digest',
};
export const pfAiAgentLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(PF_AI_AGENT_LABEL, v) ? PF_AI_AGENT_LABEL[v as PfAiAgent] : '—';

/**
 * Review outcome (agent_runs.outcome). A pending draft reads as WAITING ON A
 * PERSON, never as anything published. Unknown → `—`, never a guessed state.
 */
export const PF_AI_OUTCOME_SKIN: Record<string, PfSkin> = {
  pending: { label: 'Awaiting your approval', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  actioned: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  dismissed: { label: 'Dismissed', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  superseded: { label: 'Superseded', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  no_action: { label: 'No action', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const pfAiOutcomeSkin = (v: string | null | undefined): PfSkin =>
  v && Object.prototype.hasOwnProperty.call(PF_AI_OUTCOME_SKIN, v) ? PF_AI_OUTCOME_SKIN[v] : PF_UNKNOWN_SKIN;

/** A compliance item's state (lib/pf-ai-digests.ts expiryState + the day limit). */
export const PF_AI_STATE_SKIN: Record<string, PfSkin> = {
  expired: { label: 'Expired', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  missing: { label: 'Missing', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  day_limit_reached: { label: 'Day limit reached', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
};
export const pfAiStateSkin = (v: string | null | undefined): PfSkin =>
  v && Object.prototype.hasOwnProperty.call(PF_AI_STATE_SKIN, v) ? PF_AI_STATE_SKIN[v] : PF_UNKNOWN_SKIN;

export const PF_DIG_EVENT_LABEL: Record<string, string> = { excavated: 'Excavated', backfilled: 'Backfilled' };
export const pfDigEventLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(PF_DIG_EVENT_LABEL, v) ? PF_DIG_EVENT_LABEL[v] : '—';

/** What a stored run covered, from its trigger data. Unknown → `—`. */
export function pfAiScopeText(agent: string, triggerData: Record<string, unknown> | null, day: (v: string | null) => string): string {
  const t = triggerData ?? {};
  const str = (k: string) => (typeof t[k] === 'string' && t[k] ? (t[k] as string) : null);
  if (agent === 'pf_daily_summary') {
    const job = str('jobNumber');
    const wd = str('workDate');
    return job && wd ? `${job} · ${day(wd)}` : job ?? (wd ? day(wd) : '—');
  }
  if (agent === 'pf_ticket_preflight') return str('ticketNumber') ?? (str('jobNumber') ? `${str('jobNumber')} (whole job)` : '—');
  if (agent === 'pf_compliance_digest') return str('today') ? `As of ${day(str('today'))}` : '—';
  return '—';
}

/** The API's edit limits on approve (routes/pipeline-facility-ai.routes.ts). Checked there; mirrored here to say so first. */
export const PF_AI_MAX_NARRATIVE = 2000;
export const PF_AI_MAX_OBSERVATIONS = 8;
export const PF_AI_MAX_OBSERVATION = 400;

/**
 * A refusal from the AI routes, in the API's own words. These routes answer
 * `{ error, code }` and every `error` is already a sentence the office can
 * read ("Job PFJ-0001 has no daily log for October 3.", "This draft is
 * already actioned."), so it is shown as sent. A 403 is a refusal whatever
 * the body says; a bare 404 says the record was not found.
 */
export function pfAiRefusal(err: unknown, fallback: string): string {
  const e = pfApiError(err);
  if (e.status === 403) return 'You do not have permission to do this. AI drafts are for admins, office staff and supervisors.';
  if (e.message) return e.message;
  return pfRefusal(err, fallback);
}
